import React, { useState, useContext, useEffect } from 'react';
import useSWR from 'swr';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Zoom,
  IconButton,
  CircularProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Snackbar } from 'Components';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import PasswordValidator from 'password-validator';
import PasswordRequirements from 'Pages/public/authentication/newSign-in/PasswordRequirements';
import { Input } from '../Input';
import styles from './PasswordEditModal.module.css';

const passwordSchema = new PasswordValidator()
  .is()
  .min(12)
  .has()
  .symbols(1)
  .has()
  .digits(1)
  .has()
  .uppercase(1)
  .lowercase(1);
const defaultSnackbar = {
  isVisible: false,
  type: undefined,
  message: undefined,
};
const PasswordEditModal = ({
  isVisible,
  onHide: onClose,
  title,
  currentPasswordLabel,
  newPasswordLabel,
  passwordRule,
  resetCodeLabel,
  subtitle
}) => {
  const { langText, userDetails } = useContext(AppContext);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
const email = userDetails?.email;
  const [resetCodeSent, setResetCodeStatus] = useState(false);
  const commonText = langText?.common;
  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const hasUpperCase = /[A-Z]/.test(newPassword);
  const passwordHealth = newPassword
    ? newPassword.length < 12 || !hasUpperCase
      ? 'weak'
      : passwordSchema.validate(newPassword)
      ? newPassword.length >= 12
        ? 'strong'
        : 'medium'
      : 'weak'
    : '';
  const { isValidating: sendingResetCode, mutate: sendResetCode } = useSWR([endpoints.forgotPasswordEmail, email], {
    fetcher: (url, inputEmail) => dataFetcher(url, { email: inputEmail }),
    onSuccess: ({ success, message }) => {
      if (success) {
        setResetCodeStatus(true);
        setSnackbar({ isVisible: true, type: 'error', message, duration: 2500 });
      } else {
        setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while sending Reset Code' });
      }
    },
    onError: () => {
      setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while sending Reset Code' });
    },
  });
  const onHide = () => {
    setCurrentPassword('');
    setResetCode('');
    setResetCodeStatus(false);
    setNewPassword('');
    setSnackbar(defaultSnackbar);
    onClose();
  };
  useEffect(() => { if (isVisible) { sendResetCode(); } }, [isVisible, sendResetCode]);
  const {
    isValidating: resettingPassword,
    mutate: resetPassword,
  } = useSWR([endpoints.updatePasswordWithCode, email, newPassword, resetCode], {
    fetcher: (url, inputEmail, inputPassword, inputResetCode) => dataFetcher(url, {
      email: inputEmail,
      password: inputPassword,
      reset_code: inputResetCode,
    }),
    onSuccess: ({ success, message }) => {
      if (success) {
        setSnackbar({ isVisible: true, message, type: 'success', duration: 2500 });
        onClose();
        setTimeout(() => {
          setCurrentPassword('');
          setResetCode('');
          setResetCodeStatus(false);
          setNewPassword('');
          setSnackbar(defaultSnackbar);
        }, 300);
      } else {
        setSnackbar({ isVisible: true, message, type: 'error' });
      }
    },
    onError: (error) => {
      setSnackbar({ isVisible: true, message: error.response.data.message, type: 'error' });
    },
  });
  return (
    <>
      <Dialog
        open={isVisible}
        onClose={onHide}
        TransitionComponent={Zoom}
        maxWidth="xs"
        fullWidth
        classes={{ paper: styles.dialogPaper }}
      >
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>{title}</h2>
            {subtitle && <p className={styles.modalSubtitle}>{subtitle}</p>}
          </div>
          <IconButton onClick={onHide} className={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className={styles.modalContent}>
          <div className={styles.formGroup}>
            <label className={styles.label}>{currentPasswordLabel}</label>
            <Input
              noLable
              auto-complete="one-time-code"
              className={styles.inputField}
              containerClassName={styles.inputFieldContainer}
              onInput={(e) => {
                const {
                  target: { value: inputResetCode },
                } = e;
                setResetCode(inputResetCode);
              }}
              type="code"
              maxLength={5}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>{newPasswordLabel}</label>
            <Input
              noLable
              className={styles.inputField}
              containerClassName={styles.inputFieldContainer}
              maxLength={newPassword && 30}
              minLength={12}
              onPaste={(e) => {
                const pastedText = (
                  e.clipboardData || window.clipboardData
                ).getData('text');
                // Check if the pasted text exceeds 30 characters
                if (pastedText.length > 30) {
                  // alert('Password cannot exceed 30 characters.');
                  e.preventDefault();
                }
                const totalLength = e.target.value.length + pastedText.length;
                if (totalLength > 30) {
                  e.preventDefault();
                }
              }}
              onInput={(e) => {
                const {
                  target: { value: inputNewPassword },
                } = e;
                // Check if the length of inputNewPassword exceeds 30 characters
                if (inputNewPassword.length > 30) {
                  // alert('Password cannot exceed 30 characters.');
                  // Trim the password to the maximum length
                  setNewPassword(inputNewPassword.slice(0, 30));
                } else {
                  setNewPassword(inputNewPassword);
                }
              }}
              type="password"
            />
          </div>

          <div className={styles.passwordRequirementsContainer}>
            <PasswordRequirements password={newPassword} />
          </div>
        </DialogContent>

        <DialogActions className={styles.modalActions}>
          <button
            className={styles.cancelButton}
            onClick={onHide}
            disabled={resettingPassword}
          >
            {commonText?.cancel || 'Cancel'}
          </button>
          <button
            className={styles.updateButton}
            onClick={resetPassword}
            disabled={
              !(passwordHealth === 'strong' || passwordHealth === 'medium')
              || resettingPassword
              || resetCode.length < 5
            }
          >
            {resettingPassword ? (
              <>
                <CircularProgress size={16} className={styles.buttonLoader} />
                {commonText?.updating || 'Updating'}
                ...
              </>
            ) : (
              commonText?.update || 'Update'
            )}
          </button>
        </DialogActions>
      </Dialog>

      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || ''}
        onClose={() => {
          setSnackbar(defaultSnackbar);
        }}
        type={snackbar?.type}
      />
    </>
  );
};
export default PasswordEditModal;
